<template>
  <div class="delete-account-page">
    <AccountPageTopBar :title="$t('deleteAccount.title')" @back="goBack" />

    <main class="delete-account-page__content">
      <section class="delete-account-hero">
        <span class="delete-account-hero__icon">
          <img src="@/assets/img/profile/account/delete-document.svg" alt="">
          <i><img src="@/assets/img/profile/account/delete.svg" alt=""></i>
        </span>
        <h1>{{ $t('deleteAccount.confirmTitle') }}</h1>
        <p>{{ $t('deleteAccount.confirmDescription') }}</p>
      </section>

      <section class="delete-account-notice">
        <header><Icon name="info-o" /> {{ $t('deleteAccount.noteTitle') }}</header>
        <p>• <span>{{ $t('deleteAccount.noteOrder') }}</span></p>
        <p>• <span>{{ $t('deleteAccount.noteData') }}</span></p>
      </section>

      <section class="delete-account-verification">
        <h2>{{ $t('deleteAccount.smsTitle') }}</h2>
        <p>{{ $t('deleteAccount.smsDescription') }}</p>
        <strong>{{ maskedMobile || '--' }}</strong>

        <div class="delete-account-code">
          <input
            v-model="code"
            type="tel"
            inputmode="numeric"
            maxlength="6"
            autocomplete="one-time-code"
            :placeholder="$t('deleteAccount.codePlaceholder')"
            @input="onCodeInput"
          >
          <span></span>
          <button
            type="button"
            :disabled="sendingCode || countdown > 0"
            @click="sendCode"
          >
            {{ sendCodeLabel }}
          </button>
        </div>
        <small>{{ $t('deleteAccount.codeValidTip') }}</small>

        <Checkbox v-model="agreed" class="delete-account-agreement" shape="round" checked-color="#0b45ff">
          <span>{{ $t('deleteAccount.agreementPrefix') }}</span>
          <button
            v-for="(agreement, index) in displayAgreements"
            :key="agreement.id || index"
            type="button"
            @click.stop="openAgreement(agreement, index)"
          >
            {{ agreementTitle(agreement, index) }}<template v-if="index < displayAgreements.length - 1">, </template>
          </button>
        </Checkbox>

        <button
          class="delete-account-submit"
          type="button"
          :disabled="code.length !== 6 || submitting"
          @click="confirmSubmit"
        >
          {{ submitting ? $t('loading') : $t('confirm') }}
        </button>
      </section>
    </main>

    <Popup
      v-model="agreementVisible"
      class="delete-agreement-preview"
      :close-on-click-overlay="false"
    >
      <header>
        <h2>{{ selectedAgreementTitle }}</h2>
        <button type="button" :aria-label="$t('close')" @click="agreementVisible = false">
          <Icon name="cross" />
        </button>
      </header>
      <iframe
        :srcdoc="selectedAgreementHtml"
        sandbox=""
        frameborder="0"
        :title="selectedAgreementTitle"
      ></iframe>
    </Popup>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { Checkbox, Dialog, Icon, Popup } from 'vant';
import { getAgreements } from '@/api/common';
import { cancelMember, getMemberInfo } from '@/api/member';
import AccountPageTopBar from '@/components/AccountPageTopBar/index.vue';
import routerBack from '@/mixins/routerBack';

const DELETE_ACCOUNT_COMPLETED_KEY = 'accountDeleteCompleted';

export default {
  name: 'ProfileDeleteAccount',
  mixins: [routerBack],
  components: {
    AccountPageTopBar,
    Checkbox,
    Icon,
    Popup,
  },
  data() {
    return {
      mobile: '',
      agreements: [],
      code: '',
      agreed: false,
      sendingCode: false,
      submitting: false,
      countdown: 0,
      countdownTimer: null,
      agreementVisible: false,
      selectedAgreementTitle: '',
      selectedAgreementHtml: '',
    };
  },
  computed: {
    ...mapState('user', ['session', 'userInfo']),
    isLogin() {
      return !!this.session?.accessToken;
    },
    normalizedMobile() {
      const digits = String(this.mobile || this.userInfo?.phoneNumber || '').replace(/\D/g, '');
      if (!digits) return '';
      if (digits.startsWith('62')) return digits;
      if (digits.startsWith('0')) return `62${digits.slice(1)}`;
      return `62${digits}`;
    },
    maskedMobile() {
      if (!this.normalizedMobile) return '';
      if (this.normalizedMobile.length <= 5) return `+${this.normalizedMobile}`;
      return `+${this.normalizedMobile.slice(0, 5)} **** ****`;
    },
    displayAgreements() {
      if (this.agreements.length) return this.agreements;
      return [{
        title: this.$t('deleteAccount.agreementName'),
        content: '',
      }];
    },
    sendCodeLabel() {
      if (this.sendingCode) return this.$t('loading');
      if (this.countdown > 0) return this.$t('deleteAccount.codeCountdown', { seconds: this.countdown });
      return this.$t('deleteAccount.getCode');
    },
  },
  mounted() {
    if (!this.isLogin) {
      this.requireLogin();
      return;
    }
    this.mobile = this.userInfo?.phoneNumber || '';
    this.loadInitialData();
  },
  beforeDestroy() {
    this.clearCountdown();
  },
  methods: {
    requireLogin() {
      Dialog.confirm({
        title: this.$t('myPage.loginRequiredTitle'),
        message: this.$t('myPage.loginRequiredContent'),
        confirmButtonText: this.$t('confirm'),
        cancelButtonText: this.$t('cancel'),
      })
        .then(() => {
          this.$router.replace({
            path: '/login',
            query: { redirect: '/profile/delete-account' },
          });
        })
        .catch(() => this.$router.replace('/profile/settings'));
    },
    async loadInitialData() {
      await Promise.all([this.loadMemberInfo(), this.loadAgreements()]);
    },
    async loadMemberInfo() {
      try {
        const res = await getMemberInfo({ _showLoading: false });
        if (res?.code !== 'Success') throw new Error(res?.message || '');
        this.mobile = res?.data?.mobile || this.mobile;
      } catch (error) {
        if (!this.mobile) this.$toast(error?.message || this.$t('accountSettings.loadFailed'));
      }
    },
    async loadAgreements() {
      try {
        const res = await getAgreements(
          { sceneCode: 'DELETE_ACCOUNT' },
          { _showLoading: false }
        );
        if (res?.code !== 'Success') throw new Error(res?.message || '');
        this.agreements = Array.isArray(res?.data?.agreements) ? res.data.agreements : [];
      } catch (error) {
        this.agreements = [];
        this.$toast(error?.message || this.$t('accountSettings.loadFailed'));
      }
    },
    onCodeInput(event) {
      this.code = String(event.target.value || '').replace(/\D/g, '').slice(0, 6);
    },
    async sendCode() {
      if (!this.agreed) {
        this.$toast(this.$t('deleteAccount.agreementRequired'));
        return;
      }
      if (!this.normalizedMobile) {
        this.$toast(this.$t('accountSettings.loadFailed'));
        return;
      }
      if (this.sendingCode || this.countdown > 0) return;

      this.sendingCode = true;
      try {
        await this.$store.dispatch('user/getCode', {
          mobile: this.normalizedMobile,
          scene: 'cancel',
        });
        this.startCountdown();
      } catch (error) {
        this.$toast(error?.message || this.$t('deleteAccount.codeFailed'));
      } finally {
        this.sendingCode = false;
      }
    },
    startCountdown() {
      this.clearCountdown();
      this.countdown = 60;
      this.countdownTimer = window.setInterval(() => {
        this.countdown -= 1;
        if (this.countdown <= 0) this.clearCountdown();
      }, 1000);
    },
    clearCountdown() {
      if (this.countdownTimer) window.clearInterval(this.countdownTimer);
      this.countdownTimer = null;
      if (this.countdown < 0) this.countdown = 0;
    },
    confirmSubmit() {
      if (this.code.length !== 6 || this.submitting) return;
      if (!this.agreed) {
        this.$toast(this.$t('deleteAccount.agreementRequired'));
        return;
      }
      Dialog.confirm({
        title: this.$t('deleteAccount.submitConfirmTitle'),
        message: this.$t('deleteAccount.submitConfirmDescription'),
        confirmButtonText: this.$t('confirm'),
        cancelButtonText: this.$t('cancel'),
        confirmButtonColor: '#ff2d16',
      })
        .then(this.submitDeleteAccount)
        .catch(() => {});
    },
    async submitDeleteAccount() {
      if (this.submitting) return;
      this.submitting = true;
      try {
        const res = await cancelMember({ code: this.code });
        if (res?.code !== 'Success') throw new Error(res?.message || '');
        sessionStorage.setItem(DELETE_ACCOUNT_COMPLETED_KEY, '1');
        this.$router.replace('/profile/delete-account/success');
      } catch (error) {
        this.$toast(error?.message || this.$t('deleteAccount.failed'));
      } finally {
        this.submitting = false;
      }
    },
    agreementTitle(agreement, index) {
      return agreement?.title || (index === 0 ? this.$t('deleteAccount.agreementName') : this.$t('accountSettings.deviceRentalAgreement'));
    },
    decodeAgreementContent(content) {
      const value = String(content || '');
      if (!value) return `<p>${this.$t('accountSettings.noAgreementContent')}</p>`;
      try {
        const binary = window.atob(value);
        const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));
        if (window.TextDecoder) return new TextDecoder('utf-8').decode(bytes);
        return decodeURIComponent(Array.from(bytes, byte => `%${byte.toString(16).padStart(2, '0')}`).join(''));
      } catch {
        return value;
      }
    },
    openAgreement(agreement, index) {
      this.selectedAgreementTitle = this.agreementTitle(agreement, index);
      const content = this.decodeAgreementContent(agreement?.content);
      this.selectedAgreementHtml = `<!doctype html><html><head><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:0;padding:16px;color:#111827;font-family:Arial,sans-serif;font-size:15px;line-height:1.65;overflow-wrap:anywhere}img{max-width:100%;height:auto}</style></head><body>${content}</body></html>`;
      this.agreementVisible = true;
    },
  },
};
</script>

<style lang="less" scoped>
.delete-account-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #ffffff 0%, #f4f8ff 100%);
  color: #070d3f;
}

.delete-account-page__content {
  padding: 112px 16px calc(48px + env(safe-area-inset-bottom));
}

.delete-account-hero {
  display: flex;
  align-items: center;
  flex-direction: column;
  text-align: center;

  h1 {
    margin: 28px 0 0;
    font-size: 30px;
    font-weight: 600;
    line-height: 44px;
  }

  p {
    max-width: 620px;
    margin: 8px 0 0;
    color: #6b7280;
    font-size: 26px;
    line-height: 36px;
  }
}

.delete-account-hero__icon {
  position: relative;
  display: flex;
  width: 144px;
  height: 144px;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  background: #eaf3ff;
  color: #ffffff;
  font-size: 96px;

  > img {
    width: 96px;
    height: 96px;
  }

  > i {
    position: absolute;
    right: -8px;
    bottom: -8px;
    display: flex;
    width: 96px;
    height: 96px;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #0b45ff;
    color: #ffffff;
    font-size: 48px;
    font-style: normal;

    img {
      width: 48px;
      height: 48px;
    }
  }
}

.delete-account-notice {
  margin-top: 16px;
  padding: 16px;
  border-radius: 20px;
  background: #eaf3ff;

  header {
    display: flex;
    align-items: center;
    gap: 12px;
    color: #0b45ff;
    font-size: 30px;
    font-weight: 600;
    line-height: 48px;
  }

  p {
    display: flex;
    margin: 16px 0 0;
    gap: 12px;
    color: #6b7280;
    font-size: 26px;
    line-height: 36px;

    span {
      flex: 1;
    }
  }
}

.delete-account-verification {
  margin-top: 16px;
  padding: 24px;
  border: 2px solid #e5eaf3;
  border-radius: 20px;
  background: #ffffff;

  h2 {
    margin: 0;
    font-size: 30px;
    font-weight: 500;
    line-height: 44px;
  }

  > p {
    margin: 16px 0 0;
    color: #6b7280;
    font-size: 26px;
    line-height: 36px;
  }

  > strong {
    display: block;
    margin-top: 24px;
    font-size: 30px;
    font-weight: 500;
    line-height: 44px;
  }

  > small {
    display: block;
    margin-top: 16px;
    color: #6b7280;
    font-size: 24px;
    line-height: 32px;
  }
}

.delete-account-code {
  display: flex;
  height: 96px;
  margin-top: 24px;
  align-items: center;
  overflow: hidden;
  border: 2px solid #dde5f2;
  border-radius: 20px;

  input {
    min-width: 0;
    height: 100%;
    padding: 0 24px;
    flex: 1;
    border: 0;
    outline: none;
    color: #070d3f;
    font-size: 32px;
    box-sizing: border-box;

    &::placeholder {
      color: #9ca3af;
      font-size: 26px;
    }
  }

  > span {
    width: 2px;
    height: 56px;
    background: #e5eaf3;
  }

  button {
    width: 208px;
    height: 100%;
    padding: 0 12px;
    border: 0;
    background: #ffffff;
    color: #0b45ff;
    font-size: 24px;
    font-weight: 500;

    &:disabled {
      color: #9ca3af;
    }
  }
}

::v-deep .delete-account-agreement {
  margin-top: 24px;
  align-items: flex-start;

  .van-checkbox__icon {
    margin-top: 2px;
  }

  .van-checkbox__label {
    margin-left: 12px;
    color: #070d3f;
    font-size: 26px;
    line-height: 40px;
  }

  button {
    display: inline;
    padding: 0;
    border: 0;
    background: transparent;
    color: #0b45ff;
    font: inherit;
  }
}

.delete-account-submit {
  width: 100%;
  height: 92px;
  margin-top: 24px;
  border: 0;
  border-radius: 20px;
  background: linear-gradient(90deg, #0b45ff, #2778ff);
  color: #ffffff;
  font-size: 30px;
  font-weight: 500;

  &:disabled {
    background: #dbe5f5;
    color: #9ca3af;
  }
}

::v-deep .delete-agreement-preview {
  display: flex;
  width: calc(100% - 64px);
  max-width: 686px;
  height: calc(100% - 64px);
  flex-direction: column;
  overflow: hidden;
  border-radius: 32px;
  background: #ffffff;

  > header {
    display: flex;
    min-height: 96px;
    align-items: center;
    padding: 16px 16px 16px 32px;
    border-bottom: 2px solid #e5eaf3;

    h2 {
      min-width: 0;
      margin: 0;
      flex: 1;
      overflow: hidden;
      font-size: 36px;
      font-weight: 600;
      line-height: 52px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    button {
      width: 80px;
      height: 80px;
      border: 0;
      background: transparent;
      color: #070d3f;
      font-size: 40px;
    }
  }

  > iframe {
    width: 100%;
    min-height: 0;
    flex: 1;
  }
}
</style>
